'use client'

import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

const AUTH_URL = process.env.NEXT_PUBLIC_VC_AUTH_URL ?? 'https://auth.voltcharge.lk'

function getLoginUrl() {
  const returnTo = `${window.location.origin}/auth/callback`
  return `${AUTH_URL}/api/login?app=ERP&return_to=${encodeURIComponent(returnTo)}`
}

/**
 * The API says which sign-in is active (SSO_FLOW, runtime): 'oidc' = vc-auth
 * auth-code + PKCE run by the API (ES256 id_token, no shared secret —
 * vc-platform#108); otherwise the v1 redirect above. Any error → v1.
 */
async function resolveLoginUrl(): Promise<string> {
  try {
    const apiUrl = process.env.NEXT_PUBLIC_API_URL ?? ''
    const r = await fetch(`${apiUrl}/api/v1/auth/sso-flow`, { cache: 'no-store' })
    if (r.ok && ((await r.json()) as { flow?: string }).flow === 'oidc') {
      return `${apiUrl}/api/v1/auth/oidc/start?next=/`
    }
  } catch {
    /* fall through to v1 */
  }
  return getLoginUrl()
}

export default function LoginClient({ signedOut }: { signedOut: boolean }) {
  const [redirecting, setRedirecting] = useState(false)

  useEffect(() => {
    if (signedOut) return
    resolveLoginUrl().then((u) => window.location.replace(u))
  }, [signedOut])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-full max-w-md space-y-8 px-4">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">V</span>
            </div>
            <span className="text-2xl font-bold">
              <span className="text-red-600">VOLT</span>
              <span className="text-gray-800"> CHARGE</span>
            </span>
          </div>
          <p className="text-sm text-gray-500">VOLT INDUSTRIAL COMPANY (PVT) LTD</p>
        </div>

        {signedOut ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-center text-xl">You&apos;ve been signed out</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center gap-4">
              <p className="text-sm text-gray-500 text-center">
                Sign in again to continue using VoltCharge ERP.
              </p>
              <Button
                className="w-full bg-red-600 hover:bg-red-700 text-white"
                onClick={() => {
                  setRedirecting(true)
                  void resolveLoginUrl().then((u) => { window.location.href = u })
                }}
                disabled={redirecting}
              >
                {redirecting ? 'Redirecting…' : 'Sign in'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-center text-xl">Redirecting to VoltCharge ID…</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-center text-gray-500">
                If you&apos;re not redirected automatically,{' '}
                <a href={`${AUTH_URL}/api/login?app=ERP`} className="underline text-red-600">
                  click here to sign in
                </a>
                .
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
